import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import moment, { Moment } from 'moment'
import { isEmpty } from 'underscore'
import { ADVANCED_ANALYTICS, IPlotVariable, IBestDataset } from '../../../../types/analytics'
import { IEnrichedMetadata } from '../../../../types/metadata'
import { IAdvancedAnalyticsApiPayload } from '../../../../types/chart'
import { DT0Format } from '../../../../utils/StaticVariable'
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput'
import { MarkerItem } from '../../../../types/home'
import Api from '../../../../api/Api'
import useDatasetStore from '../../../../store/datasetStore'
import { usePointStore } from '../../../../store/pointStore'
import useAnalyticsStore from '../../../../store/analyticsStore'

interface UseHistogramParams {
  coordinates: number[]
  previewDatasetId: string
  datasets: IBestDataset[]
  dateRange: [moment.Moment, moment.Moment]
  enrichedMetadata: IEnrichedMetadata[]
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void
  histogramImage: string
  histogramLinks: string[]
  error: string
  updating: boolean
  pointName: string
  userInput: IAnalytcisUserInput
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>
  onDatasetChange: (item: string) => void
  point?: MarkerItem
  globalDateRange: any[]
  setGlobalDateRange: (item: any) => void
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void,
  gridCellSize: string,
  waterDepth: string,
}

export const useHistogramParams = (params: UseHistogramParams) => {
  const {
    coordinates,
    previewDatasetId,
    datasets,
    dateRange,
    enrichedMetadata,
    onUpdate,
    histogramImage,
    pointName,
    userInput,
    updateUserInput,
    onDatasetChange,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
    gridCellSize,
    error,
    waterDepth,
  } = params

  const selfTriggered = useRef(false)

  const [dataset, setDataset] = useState(userInput.Histogram.dataset)
  const [directionalDataset, setDirectionalDataset] = useState(userInput.Histogram.datasetY)
  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([])
  const [variablesY, setVariablesY] = useState<IPlotVariable[] | null>(userInput.Histogram.variablesY)
  const [variableX, setVariableX] = useState<IPlotVariable | null>(userInput.Histogram.variableX)
  const [variableY, setVariableY] = useState<IPlotVariable | null>(userInput.Histogram.variableY)
  const [directionalInterval, setDirectionalInterval] = useState(userInput.Histogram.directionalInterval)
  const [magnitudeInterval, setMagnitudeInterval] = useState(userInput.Histogram.magnitudeInterval)
  const [month, setMonth] = useState(userInput.Histogram.month)
  const [startDate, setStartDate] = useState(userInput.Histogram.startDate || moment(dateRange[0]))
  const [endDate, setEndDate] = useState(userInput.Histogram.endDate || moment(dateRange[1]))

  const datasetBriefs = useDatasetStore(state => state.datasetBriefs)
  const fwePoints = usePointStore(state => state.fwePoints)
  const setEnrichedMetadata = useAnalyticsStore(state => state.setEnrichedMetadata)

  const getImage = useCallback(
    (
      indexX: IPlotVariable,
      indexY: IPlotVariable,
      orderId: string,
      orderId2: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: magnitudeInterval,
          AnalyticType: ADVANCED_ANALYTICS.HISTOGRAM,
          DatasetId: previewDatasetId,
          Thresholds: '',
          Duration: '1',
          Percentile: 50,
          Sign1: 'below',
          OriginalDatasetId: previewDatasetId,
          enableConditional: false,
          OrderId2: orderId2,
          DatasetId2: directionalDataset ?? previewDatasetId,
          AcronymY: indexY.ShortName ? indexY.ShortName : indexY.Name,
          TitleY: indexY.Name,
          ColumnIndexY: indexY.Index,
          Thresholds2: '',
          Sign2: 'below',
          IntervalCountY: directionalInterval,
          Month: month,
          PlotFlag: '2',
        }
        onUpdate(payload)
      }
    },
    [coordinates, onUpdate, month, previewDatasetId, pointName, directionalDataset, magnitudeInterval, directionalInterval]
  )

  const onUpdateHistogram = useCallback((dateRange?: any) => {
    if (enrichedMetadata && variableX && variableY) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === dataset &&
          task.CoordinatesString === coordinates.join()
      )
      const task2 = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === directionalDataset &&
          task.CoordinatesString === coordinates.join()
      )
      if (task) {
        getImage(
          variableX,
          variableY,
          task.OrderId,
          task2?.OrderId ?? task.OrderId,
          dateRange ? dateRange[0].format(DT0Format) : startDate.format(DT0Format),
          dateRange ? dateRange[1].format(DT0Format) : endDate.format(DT0Format)
        )
      }
    }
  }, [
    enrichedMetadata,
    directionalDataset,
    magnitudeInterval,
    directionalInterval,
    directionalDataset,
    dataset,
    variableX,
    variableY,
    month,
    startDate,
    endDate,
    pointName
  ])

  const onSelectDataset = useCallback((value: string) => {
    onDatasetChange(value)
  }, [onDatasetChange])

  const doVariableXChange = useCallback((active: number) => {
    const item = variablesX.find((a: IPlotVariable) => a.Index === active)!
    setVariableX(item)
  }, [variablesX])

  const doVariableYChange = useCallback((active: number) => {
    const item = variablesY?.find((a: IPlotVariable) => a.Index === active)!
    setVariableY(item)
  }, [variablesY])

  const doMonthChange = useCallback((selectedMonth: string) => {
    setMonth(selectedMonth)
  }, [])

  const doStartDateChange = useCallback((value: any) => {
    if (value) {
      setStartDate(value)
      if (isForAllAnalytics) {
        setGlobalDateRange([value, endDate])
      }
    }
  }, [endDate, isForAllAnalytics, setGlobalDateRange])

  const doEndDateChange = useCallback((value: any) => {
    if (value) {
      setEndDate(value)
      if (isForAllAnalytics) {
        setGlobalDateRange([startDate, value])
      }
    }
  }, [startDate, isForAllAnalytics, setGlobalDateRange])

  const doDirectionalDatasetChange = useCallback(async (active: string) => {
     let metadata = enrichedMetadata.find(
          (md: IEnrichedMetadata) =>
            md.DatasetId === active &&
            md.CoordinatesString === coordinates.join()
        )
    
        if (!metadata) {
          try {
            metadata = await Api.metadata.getMetadata(active)
            const createFullExtractionTasks = await Api.service.createFullExtractionTasks(
              [metadata!],
              coordinates,
              pointName,
              gridCellSize,
              waterDepth,
              datasetBriefs,
              fwePoints,
            )
            const addEnrichedMetadata = enrichedMetadata.concat(createFullExtractionTasks)
            setEnrichedMetadata(addEnrichedMetadata)
          } catch (err) {
            console.log(err)
          }
        }
    
        const withoutTime = metadata?.ItemsInfo.filter(
          (item: IPlotVariable) => item.Name !== 'Time'
        )
        if(!withoutTime) {
          return
        }
        setVariablesY(withoutTime)
        setVariableY(withoutTime[0])
        setDirectionalDataset(active)
  }, [enrichedMetadata, coordinates, pointName, gridCellSize, waterDepth, datasetBriefs, fwePoints])

  const doDirectionalIntervalChange = useCallback((value: number | null) => {
    if(!value) return
    setDirectionalInterval(value)
  }, [])
  
  const doMagnitudeIntervalChange = useCallback((value: number | null) => {
    if(!value) return
    setMagnitudeInterval(value)
  }, [])

  const handleCheckboxChange = useCallback(() => {
    setGlobalDateRange([startDate, endDate])
    setIsForAllAnalytics(!isForAllAnalytics)
    selfTriggered.current = true
  }, [startDate, endDate, setGlobalDateRange, isForAllAnalytics, setIsForAllAnalytics])

  const disabledStartDate = useCallback((current: Moment) => {
    const isAfterEndDate = current && current > endDate
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(dateRange[0], 'day')
  }, [endDate, dateRange])

  const disabledEndDate = useCallback((current: Moment) => {
    const isBeforeStartDate = current && current < startDate
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(dateRange[1], 'day')
  }, [startDate, dateRange])

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => bd.label)
    return Array.from(new Set(label))
  }, [datasets])

  useEffect(() => {
    updateUserInput({
      Histogram: {
        variablesY: variablesY,
        datasetY: directionalDataset,
        dataset: dataset,
        month: month,
        variableX: variableX,
        variableY: variableY,
        startDate: startDate,
        endDate: endDate,
        directionalInterval: directionalInterval,
        magnitudeInterval: magnitudeInterval,
      },
    })
  }, [dataset, variableX, variableY, startDate, endDate, month, directionalInterval, magnitudeInterval, directionalDataset])

  useEffect(() => {
    if (enrichedMetadata) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === previewDatasetId &&
          task.CoordinatesString === coordinates.join()
      )
      if (task) {
        const itemsInfos = task.ItemsInfo.filter(
          (itemInfo: IPlotVariable) => itemInfo.Name !== 'Time'
        )
        if (itemsInfos.length > 1) {
          const variableList = itemsInfos.filter(
            (item) => !item.Name.includes('Direction')
          )
          const directionsList = itemsInfos.filter((item) =>
            item.Name.includes('Direction')
          )
          setVariablesX(variableList)
          if (previewDatasetId && !isEmpty(datasets) && !isEmpty(coordinates) && histogramImage === '' && error === '') {
            setVariablesY(directionsList)
            setDataset(previewDatasetId)
            setDirectionalDataset(previewDatasetId)
            const indexX = variableList[0]
            const indexY = directionsList[0]
            setVariableX(variableX || indexX)
            setVariableY(variableY || indexY)
            let dateRange = [startDate, endDate]
            if (isForAllAnalytics) {
              dateRange = globalDateRange
              setStartDate(dateRange[0])
              setEndDate(dateRange[1])
            }
            selfTriggered.current = true
            getImage(
              indexX,
              indexY,
              task.OrderId,
              task.OrderId,
              dateRange[0].format(DT0Format),
              dateRange[1].format(DT0Format),
            )
          }
        }
      }
    }
  }, [previewDatasetId, datasets, enrichedMetadata, coordinates, histogramImage, error])

  useEffect(() => {
    if (isForAllAnalytics && histogramImage !== '') {
      if (selfTriggered.current) {
        selfTriggered.current = false
        return
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateHistogram(globalDateRange)
      }
      setStartDate(globalDateRange[0])
      setEndDate(globalDateRange[1])
    }
  }, [isForAllAnalytics, histogramImage])

  return {
    dataset,
    variablesX,
    variablesY,
    variableX,
    variableY,
    month,
    startDate,
    endDate,
    categories,
    onUpdateHistogram,
    onSelectDataset,
    doVariableXChange,
    doVariableYChange,
    directionalDataset,
    doDirectionalDatasetChange,
    directionalInterval,
    doDirectionalIntervalChange,
    magnitudeInterval,
    doMagnitudeIntervalChange,
    doMonthChange,
    doStartDateChange,
    doEndDateChange,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  }
}
